import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';
import { DiscountUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { cartPerformance } from '@theme/performance';

/**
 * @typedef {Object} CartDiscountComponentRefs
 * @property {HTMLElement} cartDiscountError
 * @property {HTMLElement} cartDiscountErrorDiscountCode
 * @property {HTMLElement} cartDiscountErrorShipping
 */

/**
 * @extends {Component<CartDiscountComponentRefs>}
 */
class CartDiscount extends Component {
  requiredRefs = [
    'cartDiscountError',
    'cartDiscountErrorDiscountCode',
    'cartDiscountErrorShipping',
  ];

  /** @type {AbortController | null} */
  #activeFetch = null;

  #createAbortController() {
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }

    const abortController = new AbortController();
    this.#activeFetch = abortController;

    return abortController;
  }

  /**
   * 通常の割引コード入力フォーム
   *
   * @param {SubmitEvent} event
   */
  applyDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    const discountCodeInput = form.querySelector(
      'input[name="discount"]'
    );

    if (!(discountCodeInput instanceof HTMLInputElement)) {
      return;
    }

    await this.applyDiscountCode(discountCodeInput.value, {
      input: discountCodeInput,
      event,
      showComponentErrors: true,
    });
  };

  /**
   * 通常クーポンとVIPポイント共通の割引適用処理
   *
   * @param {string} discountCodeValue
   * @param {{
   *   input?: HTMLInputElement | null;
   *   event?: Event | null;
   *   showComponentErrors?: boolean;
   * }} options
   *
   * @returns {Promise<{
   *   success: boolean;
   *   reason?: string;
   *   alreadyApplied?: boolean;
   *   data?: Object;
   *   error?: unknown;
   * }>}
   */
  applyDiscountCode = async (
    discountCodeValue,
    options = {}
  ) => {
    const {
      input = null,
      event = null,
      showComponentErrors = false,
    } = options;

    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    if (
      typeof this.dataset.sectionId !== 'string' ||
      typeof discountCodeValue !== 'string'
    ) {
      return {
        success: false,
        reason: 'invalid_code',
      };
    }

    const normalizedDiscountCode =
      discountCodeValue.trim();

    if (!normalizedDiscountCode) {
      return {
        success: false,
        reason: 'invalid_code',
      };
    }

    const existingDiscounts =
      this.#existingDiscounts();

    const alreadyApplied =
      existingDiscounts.some((code) => {
        return (
          code.toLowerCase() ===
          normalizedDiscountCode.toLowerCase()
        );
      });

    if (alreadyApplied) {
      return {
        success: true,
        alreadyApplied: true,
      };
    }

    const abortController =
      this.#createAbortController();

    try {
      cartDiscountError.classList.add('hidden');
      cartDiscountErrorDiscountCode.classList.add(
        'hidden'
      );
      cartDiscountErrorShipping.classList.add(
        'hidden'
      );

      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: [
            ...existingDiscounts,
            normalizedDiscountCode,
          ].join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(
        Theme.routes.cart_update_url,
        {
          ...config,
          signal: abortController.signal,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          reason: 'request_error',
          data,
          error: new Error(
            data?.description ||
              data?.message ||
              '割引コードを適用できませんでした。'
          ),
        };
      }

      const discountCodes = Array.isArray(
        data.discount_codes
      )
        ? data.discount_codes
        : [];

      const appliedDiscount =
        discountCodes.find((discount) => {
          return (
            typeof discount?.code === 'string' &&
            discount.code.toLowerCase() ===
              normalizedDiscountCode.toLowerCase()
          );
        });

      if (appliedDiscount?.applicable === false) {
        if (input) {
          input.value = '';
        }

        if (showComponentErrors) {
          this.#handleDiscountError(
            'discount_code'
          );
        }

        return {
          success: false,
          reason: 'discount_code',
          data,
        };
      }

      const newHtml =
        data.sections?.[this.dataset.sectionId];

      if (typeof newHtml !== 'string') {
        return {
          success: false,
          reason: 'section_error',
          data,
          error: new Error(
            'カートの更新内容を取得できませんでした。'
          ),
        };
      }

      const parsedHtml =
        new DOMParser().parseFromString(
          newHtml,
          'text/html'
        );

      const section = parsedHtml.getElementById(
        `shopify-section-${this.dataset.sectionId}`
      );

      const discountPills =
        section?.querySelectorAll(
          '.cart-discount__pill'
        ) || [];

      if (section) {
        const renderedCodes = Array.from(
          discountPills
        )
          .map((element) => {
            if (
              !(element instanceof HTMLLIElement)
            ) {
              return null;
            }

            return (
              element.dataset.discountCode || null
            );
          })
          .filter(Boolean);

        const onlyExistingCodesRendered =
          renderedCodes.length ===
            existingDiscounts.length &&
          renderedCodes.every((renderedCode) => {
            return existingDiscounts.some(
              (existingCode) =>
                existingCode.toLowerCase() ===
                renderedCode.toLowerCase()
            );
          });

        const isShippingDiscount =
          onlyExistingCodesRendered &&
          appliedDiscount?.applicable === true;

        if (isShippingDiscount) {
          if (input) {
            input.value = '';
          }

          if (showComponentErrors) {
            this.#handleDiscountError('shipping');
          }

          return {
            success: false,
            reason: 'shipping',
            data,
          };
        }
      }

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.id)
      );

      morphSection(
        this.dataset.sectionId,
        newHtml
      );

      return {
        success: true,
        data,
      };
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'AbortError'
      ) {
        return {
          success: false,
          reason: 'aborted',
          error,
        };
      }

      console.error(
        '[CartDiscount] 割引適用エラー:',
        error
      );

      return {
        success: false,
        reason: 'request_error',
        error,
      };
    } finally {
      if (
        this.#activeFetch === abortController
      ) {
        this.#activeFetch = null;
      }

      if (event instanceof Event) {
        cartPerformance.measureFromEvent(
          'discount-update:user-action',
          event
        );
      }
    }
  };

  /**
   * 割引コードの削除
   *
   * @param {MouseEvent | KeyboardEvent} event
   */
  removeDiscount = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (
      (event instanceof KeyboardEvent &&
        event.key !== 'Enter') ||
      !(event instanceof MouseEvent) ||
      !(event.target instanceof HTMLElement) ||
      typeof this.dataset.sectionId !== 'string'
    ) {
      return;
    }

    const pill = event.target.closest(
      '.cart-discount__pill'
    );

    if (!(pill instanceof HTMLLIElement)) {
      return;
    }

    const discountCode =
      pill.dataset.discountCode;

    if (!discountCode) {
      return;
    }

    const existingDiscounts =
      this.#existingDiscounts();

    const index = existingDiscounts.findIndex(
      (code) =>
        code.toLowerCase() ===
        discountCode.toLowerCase()
    );

    if (index === -1) {
      return;
    }

    existingDiscounts.splice(index, 1);

    const abortController =
      this.#createAbortController();

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({
          discount: existingDiscounts.join(','),
          sections: [this.dataset.sectionId],
        }),
      });

      const response = await fetch(
        Theme.routes.cart_update_url,
        {
          ...config,
          signal: abortController.signal,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error(
          '[CartDiscount] 割引削除エラー:',
          data
        );

        return;
      }

      const newHtml =
        data.sections?.[this.dataset.sectionId];

      if (typeof newHtml !== 'string') {
        return;
      }

      document.dispatchEvent(
        new DiscountUpdateEvent(data, this.id)
      );

      morphSection(
        this.dataset.sectionId,
        newHtml
      );
    } catch (error) {
      if (
        error instanceof DOMException &&
        error.name === 'AbortError'
      ) {
        return;
      }

      console.error(
        '[CartDiscount] 割引削除エラー:',
        error
      );
    } finally {
      if (
        this.#activeFetch === abortController
      ) {
        this.#activeFetch = null;
      }
    }
  };

  /**
   * @param {'discount_code' | 'shipping'} type
   */
  #handleDiscountError(type) {
    const {
      cartDiscountError,
      cartDiscountErrorDiscountCode,
      cartDiscountErrorShipping,
    } = this.refs;

    const target =
      type === 'discount_code'
        ? cartDiscountErrorDiscountCode
        : cartDiscountErrorShipping;

    cartDiscountError.classList.remove('hidden');
    target.classList.remove('hidden');
  }

  /**
   * 現在適用されている割引コードを取得
   *
   * @returns {string[]}
   */
  #existingDiscounts() {
    /** @type {string[]} */
    const discountCodes = [];

    const discountPills = this.querySelectorAll(
      '.cart-discount__pill'
    );

    for (const pill of discountPills) {
      if (
        pill instanceof HTMLLIElement &&
        typeof pill.dataset.discountCode ===
          'string'
      ) {
        discountCodes.push(
          pill.dataset.discountCode
        );
      }
    }

    return discountCodes;
  }
}

if (
  !customElements.get(
    'cart-discount-component'
  )
) {
  customElements.define(
    'cart-discount-component',
    CartDiscount
  );
}